/**
 * §4 `resolveStyle`, §11 `compareCues`, §5 `mayOverwriteTiming` — the total lookups.
 *
 * All three exist because the naive version is subtly wrong in a way that only shows up
 * after a merge: a dangling style reference is routine, opaque id strings must never decide
 * render order, and an interpolation pass must never clobber a hand-set timing.
 */

import type {
  Cue,
  InputId,
  Style,
  StyleId,
  SubtitleDocument,
  TimingOrigin,
  TrackId,
} from '../../../contracts/cue-model.ts';
import { syntheticStyle } from './build.ts';

/*───────────────────────────────────────────────────────────────────────────────────────
  resolveStyle
───────────────────────────────────────────────────────────────────────────────────────*/

/**
 * TOTAL style lookup: the referenced Style, or the table's default when the reference
 * dangles. The CALLER emits diagnostic 'unknown-style-reference' when the fallback was taken,
 * which is detectable as `resolveStyle(doc, id).id !== id`.
 *
 * If even the default is missing the document is corrupt, and this still returns a usable
 * Style rather than throwing — a total function stays total, and `validate()` is the place
 * that reports the corruption.
 */
export function resolveStyle(doc: SubtitleDocument, id: StyleId): Style {
  const direct = doc.styles.byId[id];
  if (direct !== undefined) return direct;
  const fallback = doc.styles.byId[doc.styles.defaultId];
  if (fallback !== undefined) return fallback;
  return syntheticStyle(doc.styles.defaultId);
}

/** True when `resolveStyle` had to fall back — the trigger for 'unknown-style-reference'. */
export function styleReferenceDangles(doc: SubtitleDocument, id: StyleId): boolean {
  return doc.styles.byId[id] === undefined;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  compareCues
───────────────────────────────────────────────────────────────────────────────────────*/

interface OrderIndex {
  readonly tracks: ReadonlyMap<TrackId, number>;
  readonly inputs: ReadonlyMap<InputId, number>;
}

/**
 * Track/input declaration order, memoised per document OBJECT. Rebuilding it inside the
 * comparator would make a sort O(n·(t+i)); a WeakMap keyed on the immutable document makes
 * it once and lets the entry die with the document.
 */
const ORDER_CACHE = new WeakMap<SubtitleDocument, OrderIndex>();

function orderIndex(doc: SubtitleDocument): OrderIndex {
  const hit = ORDER_CACHE.get(doc);
  if (hit !== undefined) return hit;
  const tracks = new Map<TrackId, number>();
  for (const t of doc.tracks) tracks.set(t.id, t.order);
  const inputs = new Map<InputId, number>();
  for (const i of doc.inputs) inputs.set(i.id, i.order);
  const built: OrderIndex = { tracks, inputs };
  ORDER_CACHE.set(doc, built);
  return built;
}

/**
 * TOTAL sort order — (start, layer, Track.order, InputRef.order, origin.index ?? 0, id).
 *
 * Takes the DOCUMENT because track and input order live on the Track/InputRef declarations.
 * Comparing freshly-minted opaque id strings would let a bilingual merge's render order flip
 * between a fresh merge and a reloaded session. The final `id` tiebreak makes it TOTAL, so
 * writer output is stable and the diff never reports a reorder that did not happen.
 */
export function compareCues(doc: SubtitleDocument, a: Cue, b: Cue): number {
  if (a === b) return 0;
  const d0 = (a.time.start as number) - (b.time.start as number);
  if (d0 !== 0) return d0;
  const d1 = a.layer - b.layer;
  if (d1 !== 0) return d1;
  const idx = orderIndex(doc);
  const d2 = (idx.tracks.get(a.trackId) ?? 0) - (idx.tracks.get(b.trackId) ?? 0);
  if (d2 !== 0) return d2;
  const d3 = (idx.inputs.get(a.origin.inputId) ?? 0) - (idx.inputs.get(b.origin.inputId) ?? 0);
  if (d3 !== 0) return d3;
  const d4 = (a.origin.index ?? 0) - (b.origin.index ?? 0);
  if (d4 !== 0) return d4;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * Sorts a cue array by `compareCues`, returning the SAME array by reference when it is
 * already sorted. Structural sharing is contract, so a no-op sort must be a no-op.
 */
export function sortCues(doc: SubtitleDocument, cues: readonly Cue[]): readonly Cue[] {
  let sorted = true;
  for (let i = 1; i < cues.length; i++) {
    if (compareCues(doc, cues[i - 1] as Cue, cues[i] as Cue) > 0) {
      sorted = false;
      break;
    }
  }
  if (sorted) return cues;
  return [...cues].sort((x, y) => compareCues(doc, x, y));
}

/*───────────────────────────────────────────────────────────────────────────────────────
  mayOverwriteTiming
───────────────────────────────────────────────────────────────────────────────────────*/

/** manual > asr = file > interpolated. Exported as a ranked table so nobody re-derives it. */
const TIMING_RANK: Readonly<Record<TimingOrigin, number>> = {
  manual: 3,
  asr: 2,
  file: 2,
  interpolated: 1,
};

/**
 * Ranked precedence used by every pass that considers writing a timing.
 *
 * The case this exists for: an interpolation pass must never silently clobber a hand-set LRC
 * timing. `interpolated` therefore loses to everything, and only `manual` can replace
 * `manual`.
 */
export function mayOverwriteTiming(existing: TimingOrigin, incoming: TimingOrigin): boolean {
  return TIMING_RANK[incoming] >= TIMING_RANK[existing];
}
